// ============================================================================
// DÉCIDER D'UNE DÉFAILLANCE SIGNALÉE (SA-9.3) — la modale « insolv » de la maquette.
//
// ⚠️ LA VEILLE PROPOSE, L'HUMAIN DÉCIDE : trois suites (ne rien faire, poser un avertissement, contacter le client), le motif
//    obligatoire dans les trois cas. Aucun blocage fermé ne se pose d'ici : une procédure collective n'est pas un impayé,
//    et geler la caisse d'un commerçant en redressement peut l'achever.
// ⚠️ « POSER UN AVERTISSEMENT » EST PRÉSÉLECTIONNÉ, comme dans la maquette : c'est la suite qui informe l'équipe du
//    magasin sans rien lui retirer ; le motif reste à écrire, donc rien ne part sans un geste.
// ============================================================================
import { useId, useState, type KeyboardEvent } from 'react';
import { alert, rovingTarget, showToast } from '@orgazone/ui-web';
import { api, type LegalDecision, type LegalSignal } from '../api';
import { ReasonModal } from './store-reason-modal';

/** Les trois suites, dans l'ordre de la maquette, et ce que chacune fait vraiment. */
export const SUITES: { k: LegalDecision; label: string; note: string }[] = [
  { k: 'ignore', label: 'Ne rien faire', note: 'La décision est consignée avec son motif ; rien ne change pour le magasin.' },
  { k: 'warn', label: 'Poser un avertissement', note: 'L’avertissement est écartable en caisse : le magasin continue d’encaisser, l’équipe est informée. Le blocage ferme, lui, reste une décision distincte.' },
  { k: 'contact', label: 'Contacter le client', note: 'La Console consigne la décision ; le contact se fait hors de la Console.' },
];

/** La date d'une annonce, lue telle quelle (« 26/07/2026 »). */
export const dateAnnonce = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : 'date inconnue');

export function DefaillanceModal({ signal, onClose, onDone }: { signal: LegalSignal; onClose: () => void; onDone: () => void }) {
  const groupId = useId();
  const [suite, setSuite] = useState<LegalDecision>('warn');
  const source = signal.source === 'bodacc' ? `Publication BODACC du ${dateAnnonce(signal.publishedOn)}` : `Répertoire Sirene, ${dateAnnonce(signal.publishedOn)}`;
  const choisie = SUITES.find((s) => s.k === suite)!;
  // ⚠️ UN GROUPE RADIO, AU CLAVIER AUSSI : la suite choisie seule entre dans la tabulation, les flèches choisissent la voisine.
  const onSuiteKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const target = rovingTarget(e.key, i, SUITES.length, undefined, 'both');
    if (target === null) return;
    e.preventDefault();
    setSuite(SUITES[target]!.k);
    e.currentTarget.closest('[role="radiogroup"]')?.querySelectorAll<HTMLElement>('[role="radio"]')[target]?.focus();
  };
  return (
    <ReasonModal
      title={`Défaillance signalée — ${signal.tenantName}`}
      intro={`${source} : ${signal.nature.charAt(0).toLowerCase()}${signal.nature.slice(1)}${signal.court ? ` (${signal.court})` : ''}. Aucun blocage n’a été posé : une procédure collective n’est pas un impayé, et geler la caisse d’un commerçant en redressement peut l’achever.`}
      confirmLabel="Enregistrer la décision"
      busyLabel="Enregistrement…"
      placeholder={`${signal.nature} publiée le ${dateAnnonce(signal.publishedOn)} — suivi commercial engagé`}
      onClose={onClose}
      onConfirm={async (reason) => {
        try {
          const r = await api.decideLegal(signal.id, suite, reason);
          // ⚠️ L'avertissement posé mais pas encore descendu aux caisses se DIT : il est décidé, pas encore appliqué (§23.0).
          if (r.decision === 'warn' && r.propagated === false) {
            await alert({ title: 'Avertissement posé, pas encore appliqué en caisse', message: 'La décision est enregistrée ; l’avertissement descendra aux caisses à la prochaine reprise.', tone: 'err' });
          } else showToast(`Décision enregistrée — ${choisie.label.toLowerCase()}.`);
          onDone();
          return true;
        } catch (e) {
          await alert({ title: 'Décision non enregistrée', message: (e as Error).message, tone: 'err' });
          return false;
        }
      }}
    >
      {signal.url && (
        <a href={signal.url} target="_blank" rel="noopener noreferrer" className="btn-link marque" style={{ fontSize: 'var(--t-note)', display: 'inline-block', marginBottom: 12 }}>
          Lire l’annonce {signal.source === 'bodacc' ? 'au BODACC' : 'à l’annuaire des entreprises'} ↗
        </a>
      )}
      <div id={groupId} style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', margin: '0 0 6px' }}>Suite à donner</div>
      <div role="radiogroup" aria-labelledby={groupId} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        {SUITES.map((s, i) => (
          <button key={s.k} type="button" role="radio" aria-checked={suite === s.k} tabIndex={suite === s.k ? 0 : -1}
            onClick={() => setSuite(s.k)} onKeyDown={(e) => onSuiteKey(e, i)}
            className={suite === s.k ? 'chip info active' : 'chip info'}>
            {s.label}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.5, marginBottom: 12 }}>{choisie.note}</div>
      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink400)', marginBottom: 8 }}>La veille propose, elle ne décide pas : aucune publication ne déclenche seule un blocage.</div>
    </ReasonModal>
  );
}
